import React, { useState } from 'react';
import styled from 'styled-components';
import { Box } from '@origin-digital/layout';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import { interaction } from '@origin-digital/reporting-client';
import { Colors, Heading3, Link, Metrics, Modal, Text } from '@origin-digital/style-guide';
import Grid from '../../../../../../../components/MuiGrid/Grid';
import { SavingsBreakdown } from '../SavingsBreakdown';
import { RequestQuoteButton } from '../RequestQuoteButton';
import { getBienniumMonthlySystemCost, getBienniumSystemSavings } from '../solarSavingsHelper';
import { IStickyBanner } from '../../../../../../../components/StickyBanner/StickyBanner';
import { formatAsCurrency } from '../../../../../../../utils/number/numberHelper';
import { isIE11 } from '../../../../../../../utils/isIE/isIE11';

interface ISolarPackageReceipt extends IStickyBanner {
  postcode: string;
  averageBill: string;
  usagePercentDuringDay: string;
  onShowDisclaimer: () => void;
}

const useStyles = makeStyles({
  headingTopMargin: {
    marginTop: `${Metrics.Spacing.M_Y_XS}px`
  },
  info: {
    color: Colors.GREY48,
    paddingLeft: '10px'
  },
  link: {
    color: Colors.RED,
    cursor: 'pointer',
    textDecoration: 'underline'
  }
});

const SpacedText = styled(Text)`
  font-weight: 600;
  margin-bottom: ${Metrics.Spacing.M_X_XS}px;
  text-align: right;
`;

const HR = styled.hr`
  height: 2px;
  border: none;
  background-color: ${Colors.GREY8};
  margin: ${Metrics.Spacing.M_Y_LG}px 0;
`;

export const SolarPackageReceipt: React.FunctionComponent<ISolarPackageReceipt> = ({
  selected,
  onShowDisclaimer,
  averageBill,
  postcode,
  usagePercentDuringDay
}) => {
  const [modalOpen, toggleModal] = useState(false);
  const classes = useStyles({});
  const { solarSaving, solarSystem } = selected;
  // Because style guide complains about event being passed
  const controlModal = () => {
    toggleModal(!modalOpen);
    if (!modalOpen) {
      interaction('click', {
        componentType: 'link',
        id: 'modal-savings-breakdown',
        parentContainer: '',
        appName: 'solar-calc',
        label: 'How did we work this out?'
      });
    }
  };
  const monthlyCost = getBienniumMonthlySystemCost(solarSystem.totalCost, 2);
  const systemSavings = getBienniumSystemSavings(solarSaving.billSavingsForPeriod, 2);
  const panelType = `${solarSystem.systemWatts / 1000}w ${
    solarSystem.package === 'premiumPlus'
      ? 'Premium Plus'
      : solarSystem.package.charAt(0).toUpperCase() + solarSystem.package.slice(1)
  }`;
  const inverterInfo = solarSystem.package === 'advantage' ? 'GoodWe inverter' : 'Fronius inverter';
  const panelInfo =
    solarSystem.package === 'premiumPlus'
      ? `${solarSystem.solarPanels.panelCount} Trina 370w`
      : `${solarSystem.solarPanels.panelCount} JA Solar 330w`;
  return (
    <Box paddingX={8}>
      <Heading3 text="Your solar package" />

      <HR />

      <Grid
        container
        direction="row"
        justify="space-between"
        className="package-receipt-item-grid-ie"
      >
        <Grid item>
          <SpacedText size="sm" color="grey56">
            {panelType}
          </SpacedText>
        </Grid>
      </Grid>
      <Text size="sm" overflow="normal">
        {inverterInfo}
      </Text>
      <Text size="sm" overflow="normal">
        {panelInfo}
      </Text>
      <Text size="sm" overflow="normal">
        Installation costs
      </Text>
      <Text size="sm" overflow="normal">
        Price quoted is an indication only. May be affected by your roof material and location.
      </Text>

      <HR />

      <Grid
        container
        direction="row"
        justify="space-between"
        className="package-receipt-item-grid-ie"
      >
        <Grid item>
          <SpacedText size="sm" color="grey56">
            Pay upfront
          </SpacedText>
        </Grid>
        <Grid item>
          <SpacedText size="sm" color="grey56" data-id="pay-upfront-cost">
            {formatAsCurrency(solarSystem.totalCost)}
          </SpacedText>
        </Grid>
      </Grid>
      <Text size="sm">or</Text>
      <Grid container direction="row" justify="space-between" alignItems="center">
        <Grid
          container
          item
          alignItems="center"
          direction="row"
          xs={5}
          className="package-receipt-item-grid-ie"
        >
          <Grid item>
            <SpacedText size="sm" color="grey56" className={classes.headingTopMargin}>
              Payment plan
            </SpacedText>
          </Grid>
          <Grid item>
            {!isIE11() && (
              <Tooltip
                classes={{}}
                title={`Our payment plan requires $0 deposit and the remainder total cost paid over 24 months via equal monthly instalments on your credit card. Not available in all areas. The system purchased must be eligible for small-scale technology certificates (STCs) and you must assign the rights to the STCs to us. All prices include STCs and GST.
              Limit of one system per address per 6-month period unless we agree otherwise. For full terms, ask us when you call.`}
                placement="top"
              >
                <Icon className={classes.info}>info</Icon>
              </Tooltip>
            )}
          </Grid>
        </Grid>
        <Grid item xs={5} sm={5} md={5}>
          <SpacedText size="sm" color="grey56" data-id="payment-plan-cost">
            {formatAsCurrency(monthlyCost)}/month
          </SpacedText>
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        justify="space-between"
        className="package-receipt-item-grid-ie"
      >
        <Grid item>
          <Text size="sm">24 month interest free installments</Text>
        </Grid>
        <Grid item>
          <Text size="sm">$0 upfront</Text>
        </Grid>
      </Grid>

      <HR />

      <Grid
        container
        direction="row"
        justify="space-between"
        className="package-receipt-item-grid-ie"
      >
        <Grid item>
          <SpacedText size="sm" color="grey56">
            Estimated electricity saving
          </SpacedText>
        </Grid>
        <Grid item>
          <SpacedText size="sm" color="grey56" data-id="estimated-electricity-saving">
            {formatAsCurrency(systemSavings)}
          </SpacedText>
        </Grid>
      </Grid>
      <Link onClick={controlModal} className={classes.link}>
        <Text size="sm" color="red">
          How did we work this out?
        </Text>
      </Link>

      <HR />

      <Grid
        container
        direction="row"
        justify="space-between"
        className="package-receipt-item-grid-ie"
      >
        <Grid item>
          <SpacedText size="sm" color="grey56">
            Your payback period
          </SpacedText>
        </Grid>
        <Grid item>
          <SpacedText size="sm" color="grey56" data-id="payback-period">
            {solarSaving.payoffTimeYears} years
          </SpacedText>
        </Grid>
      </Grid>

      <HR />

      <Box data-id="solar-package-order">
        <RequestQuoteButton
          averageBill={averageBill}
          postcode={postcode}
          usagePercentDuringDay={usagePercentDuringDay}
          parentDataId="solar-package-order"
        />
      </Box>

      <Box marginTop={4} data-id="solar-package-request-a-call">
        <RequestQuoteButton
          averageBill={averageBill}
          postcode={postcode}
          usagePercentDuringDay={usagePercentDuringDay}
          isButton={false}
          parentDataId="solar-package-request-a-call"
        />
      </Box>

      {modalOpen && solarSaving!.billSavingsForPeriod && (
        <Modal
          accessibilityModalLabel="Savings Breakdown"
          title="How was this worked out?"
          data-id="modal-savings-breakdown"
          onDismiss={controlModal}
          transparent
        >
          <SavingsBreakdown
            savings={solarSaving!}
            onShowDisclaimer={() => {
              controlModal();
              onShowDisclaimer();
            }}
          />
        </Modal>
      )}
    </Box>
  );
};
